import React, { useEffect } from "react"
import { ReactSVG } from "react-svg"

import { graphql } from "gatsby"
import { PniArticlesFieldsFragment } from "../../../../graphql-types"

import * as styles from "./articles.module.scss"
import {
  elementOffset,
  transformItem,
  transformItemWithoutTransition,
} from "../../../components/common/functions/functions"
import { useLocale } from "../../../context/locale-context"
import useWindowWidth from "../../../hooks/use-window-width"
import arrow from "../../../../static/vectors/arrowV2.svg"
import backArrow from "../../../../static/vectors/backArrowV2.svg"
import ArticleCard from "../../../components/home/articleCard/articleCard"
import { truncateText } from "../../../tools/functions"

type ArticlesProps = {
  data: PniArticlesFieldsFragment
  projectsNewsInsightsPageSlug: string
  articles: any
  currentPage: number
  changeCurrentPage: any
  displayHighlightedArticle: boolean
}

const Articles: React.FC<ArticlesProps> = ({
  data,
  projectsNewsInsightsPageSlug,
  articles,
  currentPage,
  changeCurrentPage,
  displayHighlightedArticle,
}) => {
  const { locale, defaultLocale } = useLocale()
  const isMobile = useWindowWidth() <= 825

  const articlesPerPage = data.articlesPerPage || 7
  const articlesWithoutHighlightedArticle = articles.filter(article => article.title !== data.highlightedArticle?.title)
  const indexOfLastArticle = currentPage * articlesPerPage
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage
  const currentArticles = articlesWithoutHighlightedArticle.slice(indexOfFirstArticle, indexOfLastArticle)

  const handleScrollAnimation = (): void => {
    const renderArticles = () => {
      const allArticles = document.querySelectorAll("article")
      const articlesImages = document.getElementsByClassName("imageContainer")
      const articlesContainer =
        typeof window !== "undefined" && !!window.document && document.getElementById("allArticles")
      const articlesContainerHeight = articlesContainer && articlesContainer.clientHeight

      Array.from(allArticles).forEach((art, index) => {
        if (articlesContainer && window.scrollY >= elementOffset(art).top - articlesContainerHeight - 900) {
          !displayHighlightedArticle ? transformItemWithoutTransition(art) : transformItem(art, 400 * (index + 1))

          if (articlesImages[index]) {
            !displayHighlightedArticle
              ? transformItemWithoutTransition(articlesImages[index])
              : transformItem(articlesImages[index], 400 * (index + 1) + 200)
          }
        }
      })
    }

    renderArticles()
    document.addEventListener("scroll", () => renderArticles())
  }

  const prevPage = (): void => {
    if (currentPage > 1) {
      document.documentElement.scrollTo({ top: 0, behavior: "auto" })
      changeCurrentPage(currentPage - 1)
    }
  }

  const nextPage = (): void => {
    if (currentPage < Math.ceil(articles.length / articlesPerPage)) {
      document.documentElement.scrollTo({ top: 0, behavior: "auto" })
      changeCurrentPage(currentPage + 1)
    }
  }

  const goToPage = (number): void => {
    if (number !== currentPage) {
      document.documentElement.scrollTo({ top: 0, behavior: "auto" })
      changeCurrentPage(number)
    }
  }

  const displayPageNavigationButtons = () => {
    const totalArticles = articles.length
    let pageNumbers = []

    for (let i = 1; i <= Math.ceil(totalArticles / articlesPerPage); i++) {
      pageNumbers.push(i)
    }

    if (isMobile) {
      return (
        <nav
          className={styles.paginationContainer}
          role="navigation"
        >
          <ul className={styles.pagesContainer}>
            <button onClick={() => prevPage()} className={styles.arrow} aria-label="Previous page">
              <ReactSVG src={backArrow}/>
            </button>
            <div>
              <span
                onClick={() => goToPage(1)}
                className={styles.pageButton}
                style={{ fontWeight: "bold", backgroundColor: "#F2ECFD"}}
              >
                {currentPage}
              </span>
            </div>
            <button onClick={() => nextPage()} className={styles.arrow} aria-label="Next page">
              <ReactSVG src={arrow} />
            </button>
          </ul>
        </nav>
      )
    }

    return (
      <nav
        className={styles.paginationContainer}
        role="navigation"
      >
        <ul className={styles.pagesContainer}>
          <button onClick={() => prevPage()} className={styles.arrow} aria-label="Previous page">
            <ReactSVG src={backArrow}/>
          </button>
          {pageNumbers.map(number => {
            if (currentPage != number && pageNumbers.length > 3 && number === pageNumbers[pageNumbers.length - 2]) {
              return (
                <li className={styles.page} key={number}>
                <span className={styles.pageButton} key={number}>
                  {"..."}
                </span>
              </li>
              )
            } else {
              return (
                <li className={styles.page} key={number}>
                  <span
                    onClick={() => goToPage(number)}
                    className={styles.pageButton}
                    key={number}
                    style={{ fontWeight: currentPage === number ? "bold" : "normal", backgroundColor: currentPage === number ? "#F2ECFD" : "transparent"}}
                  >
                    {number}
                  </span>
                </li>
              )
            }
          })}
          <button onClick={() => nextPage()} className={styles.arrow} aria-label="Next page">
            <ReactSVG src={arrow} />
          </button>
        </ul>
      </nav>
    )
  }

  useEffect(() => {
    if (!isMobile) handleScrollAnimation()
  }, [indexOfFirstArticle, indexOfLastArticle])

  return (
    <section className={styles.content}>
      <div className={styles.allArticlesContainers}>
        {data.highlightedArticle && (
          <div className={styles.highlightedArticleContainer}>
          <ArticleCard
            image={data.highlightedArticle.thumbnailImage?.url ?? data.highlightedArticle.image?.url ?? ""}
            title={data.highlightedArticle.thumbnailTitle?.trim() ? data.highlightedArticle.thumbnailTitle : data.highlightedArticle.articleTitle}
            infos={truncateText(data.highlightedArticle.description, 210)}
            type={data.highlightedArticle.category?.label ?? ""}
            date={data.highlightedArticle.date ?? ""}
            bgColor={data.highlightedArticle.thumbnailBackgroundColor?.hex ?? "#162B44"}
            textColor={data.highlightedArticle.thumbnailTextColor?.hex ?? "#FFFFFF"}
            iconColor={data.highlightedArticle.thumbnailIconColor?.hex ?? "#162B44"}
            circleColor={data.highlightedArticle.thumbnailCircleColor?.hex ?? "#FFFF74"}
            link={locale === defaultLocale
              ? `/${projectsNewsInsightsPageSlug}/${data.highlightedArticle.slug}`
              : `/${locale}/${projectsNewsInsightsPageSlug}/${data.highlightedArticle.slug}`}
            double={!isMobile}
            fullscreedMode={isMobile}
          />
        </div>
        )}
        {currentArticles.map((article, index) => {
          return (
            <ArticleCard
              key={index}
              image={article.thumbnailImage?.url ?? article.image?.url ?? ""}
              title={article.thumbnailTitle?.trim() ? article.thumbnailTitle : article.articleTitle}
              infos={truncateText(article.description, 210)}
              type={article.category?.label ?? ""}
              date={article.date ?? ""}
              bgColor={article.thumbnailBackgroundColor?.hex ?? "#162B44"}
              textColor={article.thumbnailTextColor?.hex ?? "#FFFFFF"}
              iconColor={article.thumbnailIconColor?.hex ?? "#162B44"}
              circleColor={article.thumbnailCircleColor?.hex ?? "#FFFF74"}
              link={locale === defaultLocale
                ? `/${projectsNewsInsightsPageSlug}/${article.slug}`
                : `/${locale}/${projectsNewsInsightsPageSlug}/${article.slug}`}
              padding={isMobile ? "0 32px" : undefined}
            />
          )
        })}
      </div>
      {displayPageNavigationButtons()}
    </section>
  )
}

export const fragment = graphql`
  fragment PniArticlesFields on DatoCmsPniPage {
    highlightedArticle {
      ...ArticleFields
    }
    highlightedArticleTeaser
    articles {
      ...ArticleFields
    }
    articlesOrderByDate
    articlesPerPage
  }
`

export default Articles
